import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ITransformRT2, IVector2 } from '@seddi/math-v2';
import type { LogoTechnique } from '@seddi/scene';
import type { LogoAlignment } from '@/domain/logos/logoAlignment';

import type { MockupLogo, MockupScene } from '@/domain/mockup/mockupSceneTypes';

const initialState: MockupScene = {
  logos: [],
  hideTearAwayLabel: false,
};

const editorSceneLogosSlice = createSlice({
  name: 'editorSceneLogos',
  initialState,
  reducers: {
    clear: state => {
      state.logos = [];
    },
    addLogo: (state, action: PayloadAction<MockupLogo>) => {
      state.logos.push(action.payload);
    },
    removeLogo: (state, action: PayloadAction<string>) => {
      state.logos = state.logos.filter(l => l.id !== action.payload);
    },
    setLogoRegion: (state, action: PayloadAction<{ id: string; region: string }>) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      logo.region = action.payload.region;
    },
    setLogoSize: (state, action: PayloadAction<{ id: string; size: IVector2 }>) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      logo.size = action.payload.size;
    },
    setLogoRealWorldSize: (
      state,
      action: PayloadAction<{ id: string; realWorldSize?: IVector2 }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.realWorldSize = action.payload.realWorldSize;
      }
    },
    setLogoTechnique: (state, action: PayloadAction<{ id: string; technique: LogoTechnique }>) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      logo.technique = action.payload.technique;
    },
    setLogoCustomTechnique: (
      state,
      action: PayloadAction<{ id: string; customTechnique?: string }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.customTechnique = action.payload.customTechnique;
      }
    },
    setLogoTransform: (state, action: PayloadAction<{ id: string; transform: ITransformRT2 }>) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      logo.transform = action.payload.transform;
    },
    setLogoTranslation: (state, action: PayloadAction<{ id: string; translation: IVector2 }>) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      logo.transform.translation = action.payload.translation;
    },
    setLogoAlignment: (
      state,
      action: PayloadAction<{ id: string; alignment?: LogoAlignment }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (!logo) return;
      logo.alignment = action.payload.alignment;
    },
    setLogoNote: (state, action: PayloadAction<{ id: string; note?: string }>) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (!logo) return;
      let next = action.payload.note ?? '';
      next = next.replace(/\n|\r/g, '');
      if (next.length > 1000) {
        next = next.slice(0, 1000);
      }
      logo.note = next;
    },
    setLogoDisplayName: (state, action: PayloadAction<{ id: string; displayName: string }>) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (!logo) return;
      logo.displayName = action.payload.displayName;
    },
    setLogoPointOfMeasure: (
      state,
      action: PayloadAction<{ id: string; pointOfMeasure?: string }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (!logo) return;
      logo.pointOfMeasure = action.payload.pointOfMeasure;
    },
    setLogoPointOfMeasureLogoPoint: (
      state,
      action: PayloadAction<{ id: string; pointOfMeasureLogoPoint?: string }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (!logo) return;
      logo.pointOfMeasureLogoPoint = action.payload.pointOfMeasureLogoPoint;
    },
    setLogoPointOfMeasure2DName: (
      state,
      action: PayloadAction<{ id: string; pointOfMeasure2DName?: string }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (!logo) return;
      logo.pointOfMeasure2DName = action.payload.pointOfMeasure2DName;
    },
    setLogoPointOfMeasure2DDistance: (
      state,
      action: PayloadAction<{ id: string; pointOfMeasure2DDistance?: IVector2 }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (!logo) return;
      logo.pointOfMeasure2DDistance = action.payload.pointOfMeasure2DDistance;
    },
    replaceLogoById: (state, action: PayloadAction<{ id: string; logo: MockupLogo }>) => {
      const index = state.logos.findIndex(l => l.id === action.payload.id);
      if (index === -1) {
        return;
      }
      state.logos[index] = action.payload.logo;
    },
    updateLogo: (
      state,
      action: PayloadAction<{
        id: string;
        imageSrc?: string;
        imageOriginalSrc?: string;
        isMockupAsset?: boolean;
        recoloredImageSrc?: string;
        recoloredId?: string;
        colorPalette?: { originalColor: string; recoloredColor?: string; name?: string }[];
        imageSize?: IVector2;
        fileSize?: number;
        // Vector recoloring fields
        isVector?: boolean;
        originalVectorSvg?: string;
        recoloredVectorSvg?: string;
        hasMadeFullColor?: boolean;
        hasRasterContent?: boolean;
        rasterElements?: string[];
      }>,
    ) => {
      const logo = state.logos.find(l => l.id === action.payload.id);
      if (logo) {
        if (action.payload.imageSrc !== undefined) {
          logo.imageSrc = action.payload.imageSrc;
        }
        if (action.payload.imageOriginalSrc !== undefined) {
          logo.imageOriginalSrc = action.payload.imageOriginalSrc;
        }
        if (action.payload.isMockupAsset !== undefined) {
          logo.isMockupAsset = action.payload.isMockupAsset;
        }
        if (action.payload.recoloredImageSrc !== undefined) {
          logo.recoloredImageSrc = action.payload.recoloredImageSrc;
        }
        if (action.payload.recoloredId !== undefined) {
          logo.recoloredId = action.payload.recoloredId;
        }
        if (action.payload.colorPalette !== undefined) {
          logo.colorPalette = action.payload.colorPalette;
        }
        if (action.payload.imageSize !== undefined) {
          logo.imageSize = action.payload.imageSize;
        }
        if (action.payload.fileSize !== undefined) {
          logo.fileSize = action.payload.fileSize;
        }
        // Vector recoloring fields
        if (action.payload.isVector !== undefined) {
          logo.isVector = action.payload.isVector;
        }
        if (action.payload.originalVectorSvg !== undefined) {
          logo.originalVectorSvg = action.payload.originalVectorSvg;
        }
        if (action.payload.recoloredVectorSvg !== undefined) {
          logo.recoloredVectorSvg = action.payload.recoloredVectorSvg;
        }
        if (action.payload.hasMadeFullColor !== undefined) {
          logo.hasMadeFullColor = action.payload.hasMadeFullColor;
        }
        if (action.payload.hasRasterContent !== undefined) {
          logo.hasRasterContent = action.payload.hasRasterContent;
        }
        if (action.payload.rasterElements !== undefined) {
          logo.rasterElements = action.payload.rasterElements;
        }
      }
    },
    clearRecoloredLogo: (state, action: PayloadAction<string>) => {
      const logo = state.logos.find(l => l.id === action.payload);
      if (logo) {
        logo.recoloredImageSrc = undefined;
        logo.recoloredId = undefined;
        // Also clear vector SVG for vector logos
        if (logo.isVector) {
          logo.recoloredVectorSvg = undefined;
        }
      }
    },
    setHideTearAwayLabel: (state, action: PayloadAction<boolean>) => {
      state.hideTearAwayLabel = action.payload;
    },
    toggleHideTearAwayLabel: state => {
      state.hideTearAwayLabel = !state.hideTearAwayLabel;
    },
  },
});

export const {
  clear,
  addLogo,
  removeLogo,
  setLogoRegion,
  setLogoSize,
  setLogoRealWorldSize,
  setLogoTechnique,
  setLogoCustomTechnique,
  setLogoTransform,
  setLogoTranslation,
  setLogoAlignment,
  updateLogo,
  setLogoNote,
  replaceLogoById,
  setLogoDisplayName,
  setLogoPointOfMeasure,
  setLogoPointOfMeasureLogoPoint,
  setLogoPointOfMeasure2DName,
  setLogoPointOfMeasure2DDistance,
  clearRecoloredLogo,
  setHideTearAwayLabel,
  toggleHideTearAwayLabel,
} = editorSceneLogosSlice.actions;

export const editorSceneLogosReducer = editorSceneLogosSlice.reducer;
